import { useRef, useState } from "react";

import dayjs from "dayjs";
import { Printer } from "lucide-react";

import { BatchLabelPrint } from "~/components/scanning/BatchLabelPrint";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { ProductionBatch } from "~/types/production";

interface PrintLabelsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatch;
}

export function PrintLabelsDialog({
  open,
  onOpenChange,
  batch,
}: PrintLabelsDialogProps) {
  const [labelCount, setLabelCount] = useState(1);
  const [elaborationDate, setElaborationDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (printRef.current) {
      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Por favor permite ventanas emergentes para imprimir etiquetas");
        return;
      }

      // Copy the content to the new window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Etiquetas - ${batch.batch_number}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: system-ui, -apple-system, sans-serif;
              }
            </style>
          </head>
          <body>
            ${printRef.current.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        // Optionally close after print dialog
        // printWindow.onafterprint = () => printWindow.close();
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Imprimir Etiquetas
          </DialogTitle>
          <DialogDescription>
            Genera etiquetas con código de barras para el lote{" "}
            {batch.batch_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Label count and elaboration date inputs */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="labelCount">Número de etiquetas</Label>
              <Input
                id="labelCount"
                type="number"
                min={1}
                max={20}
                value={labelCount}
                onChange={(e) =>
                  setLabelCount(
                    Math.max(1, Math.min(20, parseInt(e.target.value) || 1))
                  )
                }
                className="w-24"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="elaborationDate">Fecha de Elaboración</Label>
              <Input
                id="elaborationDate"
                type="date"
                value={elaborationDate}
                onChange={(e) => setElaborationDate(e.target.value)}
                className="w-40"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Se imprimirán {labelCount} etiqueta{labelCount > 1 ? "s" : ""}{" "}
              (máximo 20)
            </p>
          </div>

          {/* Preview */}
          <div className="max-h-[400px] overflow-auto rounded-lg border bg-gray-50">
            <div className="p-4">
              <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                Vista previa
              </h4>
              <BatchLabelPrint
                ref={printRef}
                batch={batch}
                labelCount={labelCount}
                elaborationDate={elaborationDate}
              />
            </div>
          </div>

          {/* Info */}
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>
              <strong>Código de Barras:</strong> {batch.batch_number}
            </p>
            <p>
              Las etiquetas están optimizadas para impresión en stickers de
              175mm x 101mm (horizontal).
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
